import React, { useEffect, useRef } from 'react';
import {
  ButtonFormField,
  StringInputViewModel,
  submitForm,
} from '@sitecore-jss/sitecore-jss-forms';
import { JssFormData } from '@sitecore-jss/sitecore-jss-forms/types/JssFormData';
import {
  Button,
  Email,
  FieldProps,
  Form,
  ValueFieldProps,
} from '@sitecore-jss/sitecore-jss-react-forms';
import { withRouter } from 'next/router';

import { FormMessageType } from '@gn-audio/jss-core/common/enums';

import { createCustomFieldFactory } from 'components/customFieldFactory';
import { CustomFieldTypes } from 'components/customFieldTypes';
import { getValueFieldByControlName } from 'src/formHelper';
import getConfig from 'temp/config';

import { postFormMessage } from '../../utilities';

const config = getConfig();
const fieldFactory = createCustomFieldFactory();

const BasketEmailControl: React.FunctionComponent<ValueFieldProps<StringInputViewModel>> = (
  props
) => {
  const originalCssClass = props.field.model.cssClass;
  const propsWithCustomCss = {
    ...props,
    field: {
      ...props.field,
      model: {
        ...props.field.model,
        cssClass: `${originalCssClass} w-full h-full px-2 py-2 outline-none border-0 focus:shadow-tint10 rounded-none z-1`,
      },
    },
  };

  return (
    <div className="flex flex-1 flex-row items-center text-sm font-normal">
      <Email {...propsWithCustomCss} />
    </div>
  );
};

fieldFactory.setComponent(CustomFieldTypes.Email, BasketEmailControl);

const BasketButtonControl: React.FunctionComponent<FieldProps<ButtonFormField>> = ({
  field,
  onButtonClick,
  fieldFactory,
}) => {
  const originalCssClass = field.model.cssClass;
  const fieldWithCustomCss = {
    ...field,
    model: {
      ...field.model,
      cssClass: `${originalCssClass} px-11 py-2.5 bg-black text-white font-semibold uppercase transition-colors duration-200 ease-in-out text-sm border-0 cursor-pointer rounded-none hover:bg-gray-700`,
    },
  };

  return (
    <Button
      field={fieldWithCustomCss}
      onButtonClick={() => onButtonClick(field.buttonField.name)}
      fieldFactory={fieldFactory}
    />
  );
};

fieldFactory.setComponent(CustomFieldTypes.Button, BasketButtonControl);

const BasketEmailCartForm = ({ fields, router }: any) => {
  const internalSubmitHandler = useRef<(event: MessageEvent) => void>(null);

  const formFetcher = async (formData: JssFormData, endpoint: string) => {
    postFormMessage({ type: FormMessageType.PreSubmit });

    // Wait for the response
    await new Promise((resolve, reject) => {
      const submitHandler = (event: MessageEvent) => {
        const message = event.data;
        if (message?.type === FormMessageType.Submit) {
          const basketIdValueField = getValueFieldByControlName('BasketId', fields.fields);

          if (basketIdValueField?.name) {
            formData.set(basketIdValueField?.name, message.data);
          }

          window.removeEventListener('message', submitHandler);
          resolve(event.data);
        }
      };

      window.addEventListener('message', submitHandler);
      internalSubmitHandler.current = submitHandler;

      // Timeout in case no response is received
      setTimeout(() => {
        window.removeEventListener('message', submitHandler);
        reject(new Error('No Email cart response received'));
      }, 10000);
    });

    const formSubmitResult = await submitForm(formData, endpoint);
    postFormMessage({
      type: FormMessageType.AfterSubmit,
    });
    return formSubmitResult;
  };

  useEffect(() => {
    return () => {
      if (internalSubmitHandler.current) {
        window.removeEventListener('message', internalSubmitHandler.current);
      }
    };
  }, []);

  return (
    <div className="container py">
      <Form
        form={fields}
        className="relative flex flex-row border-2 border-black bg-white"
        sitecoreApiHost={''}
        sitecoreApiKey={config.sitecoreApiKey}
        language={router.locale}
        fieldFactory={fieldFactory}
        formFetcher={formFetcher}
      />
    </div>
  );
};

export default withRouter(BasketEmailCartForm);
